import { useState } from "react";
import { Loader2, X, Download } from "lucide-react";
import { downloadPhoto } from "../lib/api";
import { toast } from "sonner";

const SIZES = [
  { key: "small", label: "Small", desc: "~170px height · Fast preview" },
  { key: "medium", label: "Medium", desc: "~450px height · Blog / social" },
  { key: "large", label: "Large", desc: "~950px preview · Websites & decks" },
  { key: "original", label: "Original preview", desc: "Highest available preview (unlicensed)" },
];

export default function DownloadSizeSheet({ photo, open, onClose, onSuccess }) {
  const [busy, setBusy] = useState(null);

  if (!open) return null;

  const start = async (size) => {
    setBusy(size);
    try {
      await downloadPhoto(photo.id, size);
      toast.success("Download started", { description: `${size} · elitestock-${photo.id}-${size}.jpg` });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error("Download failed", { description: e?.message || "Please try again." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
      data-testid="download-size-sheet"
    >
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-ivory border border-border shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Download</div>
            <div className="font-serif text-lg text-ink truncate max-w-[280px]">{photo.alt || `Photo ${photo.id}`}</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center hover:bg-secondary" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-2">
          {SIZES.map((s) => (
            <button
              key={s.key}
              data-testid={`download-size-${s.key}`}
              onClick={() => start(s.key)}
              disabled={busy !== null}
              className={`w-full flex items-center gap-4 p-4 border transition-all text-left ${
                busy === s.key ? "border-ink bg-white" : "border-border bg-white hover:border-ink"
              } disabled:opacity-60`}
            >
              <span className="h-10 w-10 shrink-0 inline-flex items-center justify-center bg-secondary text-ink">
                {busy === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-ink">{s.label}</span>
                <span className="block text-xs text-muted-foreground">{s.desc}</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">.jpg</span>
            </button>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-border text-[11px] text-muted-foreground text-center">
          Photos courtesy of Depositphotos · attribution to <span className="text-ink">{photo.photographer?.name}</span> appreciated.
        </div>
      </div>
    </div>
  );
}
